import { useState, type FormEvent, type JSX, type ReactNode } from 'react';
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router';
import { usePostHog } from '@posthog/react';
import {
  Badge,
  Button,
  Card,
  ConfidenceBadge,
  Field,
  PencilIcon,
  SaveIcon,
  SparkleIcon,
  Tag,
  Textarea
} from '@viniciomendesr/rayrovisk-design-system';
import { useCreatePin, useGeneratePrompt, useProfile } from '../api/queries';
import { AccessStateScreen, accessStateOf } from '../access/AccessState';
import { ApiError } from '../api/client';
import { instagramHandle } from '../api/handle';
import { CopyToClipboard } from '../components/CopyToClipboard';
import { PromptBody } from '../components/PromptBody';
import type { GeneratedPrompt } from '../api/types';

/**
 * A label per hint the service sends, and the card draws the first one that has
 * one.
 *
 * The service stopped sending `voice` and `voice.wordsUsed` on 2026-08-06, for
 * the reason this map would have needed anyway: no field on the profile fills
 * either, so the card's "Preencher, leva 1 minuto" would open a form that
 * cannot answer it. Both ends agree, and neither is load-bearing for the other.
 *
 * The lookup still filters rather than falling back to a generic sentence,
 * because a hint this screen has no label for is one it also has no field for —
 * drawing "Dá para melhorar o seu perfil" over it would send her to hunt.
 */
const HINTS: Record<string, string> = {
  offer: 'Falta contar o que você vende',
  constraints: 'Falta dizer o que você não pode prometer'
};

/** The first hint she has a field for, or nothing — which hides the card. */
function actionableHint(hints: readonly string[]): string | undefined {
  return hints.map((hint) => HINTS[hint]).find((label) => label !== undefined);
}

const CHIPS: Array<{ label: string; field: 'tone' | 'length' | 'platform'; options: string[] }> = [
  { label: 'Tom', field: 'tone', options: ['direto', 'informal', 'conservador'] },
  { label: 'Tamanho', field: 'length', options: ['short', 'medium', 'long'] },
  { label: 'Plataforma', field: 'platform', options: ['reels', 'stories', 'carrossel'] }
];

const LENGTHS: Record<string, string> = { short: '15 s', medium: '30 s', long: '60 s' };

/**
 * Screen 08. Describe the task, get the prompt with the profile already inside.
 * Picking an adjustment regenerates against the same task rather than clearing
 * the box — losing what she typed is the fastest way to make an adjustment feel
 * expensive.
 */
export function GeneratePromptScreen(): JSX.Element {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const profile = useProfile();
  const generate = useGeneratePrompt();
  const createPin = useCreatePin();

  const [search] = useSearchParams();
  // Initial value only: after the first render the field is hers, and rewriting
  // the URL per keystroke would fight the history stack.
  const [task, setTask] = useState(() => search.get('tarefa') ?? '');
  const [adjustments, setAdjustments] = useState<Record<string, string>>({});
  const [result, setResult] = useState<GeneratedPrompt | null>(null);
  const [pinned, setPinned] = useState(false);

  const state = accessStateOf(profile.error) ?? accessStateOf(generate.error);
  if (state) {
    return <AccessStateScreen state={state} />;
  }

  // The profile is what this screen runs on, so an absent one is a redirect
  // rather than an empty form: the service answers 409 anyway.
  if (profile.isSuccess && profile.data.profile === null) {
    return <Navigate to="/gerador" replace />;
  }

  async function run(next = adjustments) {
    setPinned(false);
    try {
      const generated = await generate.mutateAsync({
        task,
        adjustments: Object.keys(next).length > 0 ? next : undefined
      });
      setResult(generated);
      posthog?.capture('prompt_generated', {
        format: generated.classification.format,
        objective: generated.classification.objective,
        fell_back: generated.classification.fellBack,
        adjustment_count: Object.keys(next).length,
      });
    } catch (error) {
      const code = error instanceof ApiError ? error.code : 'unknown';
      // `profile_required` is the interesting one: it is a buyer who reached
      // the generator before the questionnaire the whole screen runs on, and
      // the redirect below hides it from everything except this count.
      posthog?.capture('prompt_generation_failed', { code });
      if (code === 'profile_required') {
        navigate('/gerador/perfil');
      }
    }
  }

  function pick(field: string, option: string) {
    const next = { ...adjustments };
    if (next[field] === option) {
      delete next[field];
    } else {
      next[field] = option;
    }
    setAdjustments(next);
    if (result) {
      void run(next);
    }
  }

  return (
    <section className="rvk-split" style={{ maxWidth: 1180 }}>
      <div className="rvk-split-main" style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {/* "Gerar", not "Montar", from 2026-08-06.

              The D8 walk had split the two names apart and given this screen
              "Montar", on the reasoning that it assembles rather than generates
              and that "Gerar prompt" belonged to the assisted layer beside it.
              The owner reversed it: this is the screen a buyer means when she
              says she is going to gerar um prompt, and the product should be
              called what she calls it rather than what its implementation does.

              The assisted layer beside it is `Montador de prompt` now, which is
              the owner's call and not a description — the thing that assembles
              is this screen. It names an unbuilt feature, so it misleads nobody
              yet; see `AssistedPromptScreen`.

              The URL stays `/gerador/montar`: a lifetime product cannot afford
              a dead bookmark, and a rename of a label is not a reason to break
              one. */}
          <h1 style={{ margin: 0, font: '400 32px/1.12 var(--rvk-font-expressive)' }}>
            Gerar prompt
          </h1>
          <p
            style={{
              margin: 0,
              font: '400 14.5px/1.6 var(--rvk-font-functional)',
              color: 'var(--rvk-text-secondary)'
            }}
          >
            Descreva a tarefa como você falaria. O perfil entra sozinho.
          </p>
        </div>

        <Card layer="functional" padding="lg">
          <form
            onSubmit={(event: FormEvent) => {
              event.preventDefault();
              void run();
            }}
            style={{ display: 'flex', flexDirection: 'column', gap: 14 }}
          >
            <Field
              label="Descreva a tarefa"
              hint="Como você mandaria um áudio: “preciso de um reels contando por que eu mudei de nicho”."
            >
              <Textarea
                rows={3}
                autoGrow
                value={task}
                onChange={(event) => setTask(event.target.value)}
              />
            </Field>
            <div>
              <Button
                type="submit"
                variant="primary"
                loading={generate.isPending}
                iconStart={<SparkleIcon size={14} />}
              >
                Gerar prompt
              </Button>
            </div>
          </form>
        </Card>

        {generate.isError && !state && (
          <p style={{ margin: 0, font: '400 13.5px/1.5 var(--rvk-font-functional)' }}>
            {(generate.error as Error).message}
          </p>
        )}

        {result && (
          // Keyed by the generated text: a new prompt is new content, and the
          // entrance replays to say so. Same-text regenerations stay still.
          <div
            key={result.prompt}
            style={{ display: 'flex', flexDirection: 'column', gap: 20 }}
          >
            <div className="rvk-rise" style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <span
                style={{
                  font: '400 12px/1.4 var(--rvk-font-functional)',
                  color: 'var(--rvk-text-tertiary)'
                }}
              >
                Classificado como
              </span>
              <span style={{ font: '500 13px/1.4 var(--rvk-font-functional)' }}>
                {result.classification.format} · {result.classification.objective}
              </span>
              {/* GP-0: a silent fallback is what turns "unlimited" into
                  "unlimited generic". The screen says which one happened. */}
              <ConfidenceBadge level={result.classification.fellBack ? 'low' : 'high'} />
              {result.classification.fellBack && (
                <span
                  style={{
                    font: '400 12.5px/1.4 var(--rvk-font-functional)',
                    color: 'var(--rvk-text-secondary)'
                  }}
                >
                  Não reconhecemos o formato pela descrição. Diga “reels”, “carrossel” ou “legenda”
                  para afinar.
                </span>
              )}
            </div>

            {actionableHint(result.improvementHints) && (
              <div
                className="rvk-rise rvk-rise-2"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 16,
                  flexWrap: 'wrap',
                  padding: '14px 16px',
                  background: 'var(--rvk-surface-1)',
                  border: '1px solid var(--rvk-border-default)',
                  borderLeft: '3px solid var(--rvk-accent-default)',
                  borderRadius: 'var(--rvk-radius-md)'
                }}
              >
                <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0, flex: '1 1 220px' }}>
                  <span style={{ font: '500 13.5px/1.4 var(--rvk-font-functional)' }}>
                    {actionableHint(result.improvementHints)}
                  </span>
                  <span
                    style={{
                      font: '400 12.5px/1.5 var(--rvk-font-functional)',
                      color: 'var(--rvk-text-secondary)'
                    }}
                  >
                    Com essa parte preenchida, o fechamento chama a sua oferta pelo nome em vez de
                    um convite genérico.
                  </span>
                </div>
                <Link to="/gerador/perfil">
                  <Button variant="secondary" size="sm" iconStart={<PencilIcon size={14} />}>
                    Preencher, leva 1 minuto
                  </Button>
                </Link>
              </div>
            )}

            <Card className="rvk-rise rvk-rise-2" layer="functional" elevation="raised" padding="lg">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 12,
                    flexWrap: 'wrap'
                  }}
                >
                  <span
                    style={{
                      font: '600 10px/1 var(--rvk-font-functional)',
                      letterSpacing: '.13em',
                      textTransform: 'uppercase',
                      color: 'var(--rvk-text-tertiary)'
                    }}
                  >
                    Prompt montado
                  </span>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                    {pinned && (
                      <Badge tone="success" variant="subtle" size="sm">
                        Fixado
                      </Badge>
                    )}
                    <Button
                      variant="secondary"
                      size="sm"
                      loading={createPin.isPending}
                      iconStart={<SaveIcon size={14} />}
                      onClick={() => {
                        // The structured object, never `Object.values`: the server
                        // re-runs the assembler against her current profile and
                        // compares against this, which needs the same keyed shape
                        // the assembler was given, not a flattened list.
                        const structured =
                          adjustments as { tone?: string; length?: 'short' | 'medium' | 'long'; platform?: string };
                        void createPin
                          .mutateAsync({
                            kind: 'assembled',
                            label: task,
                            body: result.prompt,
                            format: result.classification.format,
                            objective: result.classification.objective,
                            adjustments: Object.keys(structured).length > 0 ? structured : undefined
                          })
                          .then(() => {
                            setPinned(true);
                            posthog?.capture('prompt_pinned', {
                              format: result.classification.format,
                              objective: result.classification.objective,
                            });
                          })
                          .catch(() => undefined);
                      }}
                    >
                      Fixar
                    </Button>
                    <CopyToClipboard
                      text={result.prompt}
                      surface="assembled"
                      sourceRef={result.skeletonId}
                    />
                  </div>
                </div>
                {createPin.isError && (
                  <p style={{ margin: 0, font: '400 13px/1.5 var(--rvk-font-functional)' }}>
                    {(createPin.error as Error).message}
                  </p>
                )}
                <PromptBody text={result.prompt} />
              </div>
            </Card>

            <Card
              className="rvk-rise rvk-rise-3"
              layer="functional"
              padding="lg"
              title="Ajustes"
              subtitle="Cada escolha remonta o prompt na hora, sem perder a tarefa descrita."
            >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, paddingTop: 4 }}>
                {CHIPS.map((group) => (
                  <div key={group.field} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <span
                      style={{
                        font: '600 9.5px/1 var(--rvk-font-functional)',
                        letterSpacing: '.14em',
                        textTransform: 'uppercase',
                        color: 'var(--rvk-text-tertiary)'
                      }}
                    >
                      {group.label}
                    </span>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                      {group.options.map((option) => (
                        <Tag
                          key={option}
                          selectable
                          selected={adjustments[group.field] === option}
                          onSelectedChange={() => pick(group.field, option)}
                        >
                          {group.field === 'length' ? LENGTHS[option] : option}
                        </Tag>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>

      <div className="rvk-split-aside" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <Card layer="functional" padding="md" title="Seu perfil" subtitle="Entra em todo prompt que você gera.">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4 }}>
            <Row label="Nicho" value={profile.data?.profile?.identity.niche ?? '—'} />
            {/* Captions when she has them, her handle when she does not.
                Since the five-step onboarding of 2026-08-06 nothing collects
                captions, so a fixed `Voz` row would read "0 trechos de legenda"
                for every new profile, beside an "Editar perfil" button that
                opens a form without the field — a number she cannot move.
                An older profile still has samples and still sees them. */}
            {(profile.data?.profile?.voice.samples.length ?? 0) > 0 ? (
              <Row
                label="Voz"
                // Singular when there is one. "1 trechos de legenda" is the sort
                // of thing that reads as nobody having looked at the screen.
                value={(() => {
                  const samples = profile.data!.profile!.voice.samples.length;
                  return `${samples} ${samples === 1 ? 'trecho' : 'trechos'} de legenda`;
                })()}
              />
            ) : (
              <Row
                label="Instagram"
                value={
                  instagramHandle(profile.data?.profile?.identity.instagram) ? (
                    <a
                      href={`https://instagram.com/${instagramHandle(
                        profile.data?.profile?.identity.instagram
                      )!.slice(1)}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: 'inherit' }}
                    >
                      {instagramHandle(profile.data?.profile?.identity.instagram)}
                    </a>
                  ) : (
                    'não preenchido'
                  )
                }
              />
            )}
            <Row
              label="Palavras evitadas"
              value={profile.data?.profile?.voice.wordsAvoided.join(' · ') || 'nenhuma'}
            />
            <Row label="Oferta" value={profile.data?.profile?.offer?.product ?? 'não preenchido'} />
            <Link to="/gerador/perfil">
              <Button variant="secondary" size="sm" fullWidth iconStart={<PencilIcon size={14} />}>
                Editar perfil
              </Button>
            </Link>
          </div>
        </Card>

        {/* "Fixados" here too. The navigation, the screen it opens and this
            card were three names for one list until the D8 walk read them
            together. */}
        <Card layer="functional" padding="md" title="Fixados">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, paddingTop: 4 }}>
            <Link to="/gerador/fixados">
              <Button variant="ghost" size="sm" iconStart={<SaveIcon size={14} />}>
                Ver os meus
              </Button>
            </Link>
          </div>
        </Card>
      </div>
    </section>
  );
}

function Row({ label, value }: { label: string; value: ReactNode }): JSX.Element {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <span
        style={{
          font: '600 9.5px/1 var(--rvk-font-functional)',
          letterSpacing: '.14em',
          textTransform: 'uppercase',
          color: 'var(--rvk-text-tertiary)'
        }}
      >
        {label}
      </span>
      <span style={{ font: '400 13px/1.45 var(--rvk-font-functional)' }}>{value}</span>
    </div>
  );
}
